import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Snackbar } from '@mui/material';
import { PersonAdd } from '@mui/icons-material';
import { apiFetch } from '../../utils/api';
import UsersTable, { UsersTableUser } from './UsersTable';
import CreateDirectUserDialog from './CreateDirectUserDialog';
import DeleteUserDialog, { DeleteTarget } from './DeleteUserDialog';

interface Props {
  onOpenRisk: (userId: string) => void;
}

const DirectUsersTab: React.FC<Props> = ({ onOpenRisk }) => {
  const [users, setUsers] = useState<UsersTableUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<any>('/getUserData');
      const arr = Array.isArray(data) ? data : (Array.isArray(data?.users) ? data.users : []);
      if (!Array.isArray(arr)) {
        console.warn('[DirectUsersTab] /getUserData returned non-array payload:', data);
      }
      setUsers(arr.map((u: any) => ({
        id: String(u.id ?? ''),
        name: String(u.name ?? '—'),
        email: String(u.email ?? '—'),
        country: String(u.country ?? ''),
        phone: String(u.phone ?? ''),
        total_deposit_inr: Number(u.total_deposit_inr ?? u.total_deposit_usd ?? 0) || 0,
        total_withdrawal_inr: Number(u.total_withdrawal_inr ?? u.total_withdrawal_usd ?? 0) || 0,
        is_locked: u.is_locked === true || u.is_locked === 't',
        sports_betlocked: u.sports_betlocked === true || u.sports_betlocked === 't',
        reg_ip: u.reg_ip ?? null,
        reg_country: u.reg_country ?? null,
        reg_city: u.reg_city ?? null,
        reg_region: u.reg_region ?? null,
        last_ip: u.last_ip ?? null,
      })).reverse());
    } catch (e: any) {
      console.error('[DirectUsersTab] /getUserData failed:', e);
      setError(e?.message || 'Failed to load direct users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}
          sx={{ mb: 2, bgcolor: 'rgba(224,27,79,0.12)', color: '#E01B4F', border: '1px solid rgba(224,27,79,0.3)' }}>
          /getUserData failed: {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          onClick={() => setCreateOpen(true)}
          variant="contained"
          startIcon={<PersonAdd />}
          sx={{ bgcolor: '#2B6EF5', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#4A85F7' } }}
        >
          Create Client / Manual ID
        </Button>
      </Box>

      <UsersTable
        users={users}
        loading={loading}
        title="Direct Users"
        onRefresh={fetchUsers}
        onOpenRisk={onOpenRisk}
        onDeleteUser={(u) => setDeleteTarget({ id: u.id, name: u.name, email: u.email })}
      />

      <DeleteUserDialog
        target={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={(t, rows) => { setNotice(`${t.name} (UID ${t.id}) deleted — ${rows} records removed`); fetchUsers(); }}
      />

      <CreateDirectUserDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(u) => { setNotice(`Client ${u.name} created (UID ${u.id})`); fetchUsers(); }}
      />

      <Snackbar
        open={notice !== null}
        autoHideDuration={5000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setNotice(null)}
          sx={{ bgcolor: 'rgba(14,204,104,0.14)', color: '#0ECC68', border: '1px solid rgba(14,204,104,0.35)' }}>
          {notice}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DirectUsersTab;
